import { ComponentRef, inject, Injectable, Type } from "@angular/core";
import { DIALOG_SERVICE_TOKEN } from "@kno2/core/theme";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import { map } from "rxjs/operators";

type OpenOptions = { header: string; dialogId?: string } & Omit<DynamicDialogConfig, "header">;

@Injectable({
    providedIn: "root"
})
export class DialogService {
    private readonly dialogService = inject(DIALOG_SERVICE_TOKEN);
    private readonly dialogRefs = new Map<string, DynamicDialogRef>();
    private readonly componentRefs = new Map<string, ComponentRef<unknown>>();

    public open<T = unknown>(component: Type<unknown>, options: OpenOptions): Promise<{ result: T }> {
        const dialogId = options.dialogId || `dialog-${options.header.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`;

        const dialogRef = this.dialogService.open(component, {
            width: "700px",
            modal: true,
            closable: true,
            position: "top",
            focusOnShow: false,
            ...options
        });

        this.dialogRefs.set(dialogId, dialogRef);

        dialogRef.onChildComponentLoaded.subscribe(() => {
            const dialogComponentRef = this.dialogService.dialogComponentRefMap?.get(dialogRef);
            if (dialogComponentRef?.instance?.componentRef) {
                this.componentRefs.set(dialogId, dialogComponentRef.instance.componentRef);
            }
        });

        return firstValueFrom(
            dialogRef.onClose.pipe(
                map((result) => {
                    this.dialogRefs.delete(dialogId);
                    this.componentRefs.delete(dialogId);
                    return { result };
                })
            )
        );
    }

    public setInput(key: string, value: unknown, dialogId?: string): void {
        const targetId = dialogId || Array.from(this.componentRefs.keys()).pop();
        if (!targetId) return;
        this.componentRefs.get(targetId)?.setInput(key, value);
    }

    public close(result?: unknown, dialogId?: string): void {
        const targetId = dialogId || Array.from(this.dialogRefs.keys()).pop();
        if (!targetId) return;

        const dialogRef = this.dialogRefs.get(targetId);
        if (!dialogRef) return;

        dialogRef.close(result);
        this.dialogRefs.delete(targetId);
    }
}
